import React, { useEffect, useState } from "react";
import { getMetricsData } from "../util/metricsModal";
import storage from "../util/storage";
import { MetricKey, trendsFor, valueText } from "./metricTrends";
import { createPreviousCache } from "./previousCache";
import { previousInterval, previousLabel } from "./previousInterval";
import { arrowOpacities, Trend, trendSentence } from "./trend";

/*
 * THE ARROWS ON THE METRICS BAR (demo build; the Footer imports this file only when REACT_APP_DEMO is "1"). Chris, 2026-10-19: "in the metrics bar at the bottom, show arrows going up or down to
 * indicate that that time interval is either up or down from the previous INTERVAL ... The arrows are 3 max either up or down and have a faded look as they go up or down."
 *
 * The Footer FEEDS this module its numbers (feed) and the range they are for; this module fetches the previous interval's numbers ONCE per range (previousCache: about one request every five
 * minutes however often the bar refreshes), works out a trend for each of the eight numbers (metricTrends, trend: the rules and the sample guard are there), and tells the arrows and the hover cards.
 * It is a small store and not a React hook because the Footer cannot call a hook that only some builds have.
 */
export interface TrendRange {
  name: string;
  startMs: number;
  endMs: number;
  /** the range runs up to now (Today, Last 7 Days...), as opposed to a custom range with its own end */
  ongoing: boolean;
}

interface FeedSnapshot {
  current: Record<string, unknown> | null | undefined;
  range: TrendRange | null;
  /** the day the account's data begins: the previous interval must lie inside it */
  earliestMs: number | null;
  /** Playback shows one past day: no arrows there (yet) */
  playback: boolean;
}

const REFRESH_MS = 5 * 60 * 1000;
const cache = createPreviousCache<Record<string, unknown>>({ ttlMs: REFRESH_MS });
const listeners = new Set<() => void>();

const state: {
  key: string;
  range: TrendRange | null;
  current: Record<string, unknown> | null | undefined;
  earliestMs: number | null;
  previous: { metrics: Record<string, unknown>; span: number; ongoing: boolean } | null;
  trends: Record<MetricKey, Trend> | null;
  signature: string;
} = { key: "", range: null, current: null, earliestMs: null, previous: null, trends: null, signature: "" };

const emit = () => listeners.forEach((fn) => fn());

/** what the arrows and cards show, so a re-render is only asked for when it changed */
const signatureOf = (trends: Record<MetricKey, Trend> | null): string => (trends ? Object.entries(trends).map(([key, t]) => `${key}:${t.direction}:${t.arrows}:${Number.isFinite(t.change) ? t.change.toFixed(3) : t.change}`).join("|") : "");

const recompute = () => {
  state.trends = state.previous && state.current ? trendsFor(state.current, state.previous.metrics) : null;
  const signature = signatureOf(state.trends);
  if (signature !== state.signature) {
    state.signature = signature;
    emit();
  }
};

const loadPrevious = (key: string) => {
  const range = state.range;
  if (!key || !range) return;
  const end = range.ongoing ? Date.now() : range.endMs;
  const previous = previousInterval(range.name, range.startMs, end, state.earliestMs);
  if (!previous.ok) {
    state.previous = null;
    recompute();

    return;
  }
  const { interval, spanDays } = previous;
  cache
    .get(`${key}|${interval.startMs}`, () => getMetricsData({ startDate: new Date(interval.startMs).toISOString(), endDate: new Date(interval.endMs).toISOString() }))
    .then((metrics) => {
      if (state.key !== key) return; // the range changed while this was on its way
      state.previous = metrics ? { metrics, span: spanDays, ongoing: range.ongoing } : null;
      recompute();
    });
};

/** The Footer's numbers and range, whenever they change. */
export const feed = (snapshot: FeedSnapshot) => {
  state.current = snapshot.current;
  state.range = snapshot.range;
  state.earliestMs = snapshot.earliestMs;
  const key = snapshot.playback || !snapshot.range ? "" : `${storage.get("siteId")}|${snapshot.range.name}|${snapshot.range.startMs}|${snapshot.range.ongoing ? "now" : snapshot.range.endMs}`;
  if (key !== state.key) {
    state.key = key;
    state.previous = null;
    loadPrevious(key);
  }
  recompute();
};

// the previous interval of a range that runs up to now moves with the clock: ask again every few minutes (the cache makes this one request, and only when its answer has gone stale)
if (typeof window !== "undefined") window.setInterval(() => loadPrevious(state.key), REFRESH_MS);

/** The metrics bar's range and numbers, and the interval before it, for another view of the SAME range (the Insights tab). A copy of what is held, read at the moment of the call. */
export const getRangeSnapshot = () => ({ range: state.range, earliestMs: state.earliestMs, current: state.current, previous: state.previous, trends: state.trends });

export const subscribe = (fn: () => void): (() => void) => {
  listeners.add(fn);

  return () => {
    listeners.delete(fn);
  };
};

/** What the hover card says about a number: its definition, then (when there is a comparison to make) how it compares with the previous interval. */
export const tipFor = (key: MetricKey, definition: string): string => {
  const trend = state.trends?.[key];
  if (!trend || !state.previous) return definition;
  const sentence = trendSentence(trend, valueText(key, state.previous.metrics), previousLabel(state.previous.span, state.previous.ongoing));

  return sentence ? `${definition} ${sentence}` : definition;
};

const CHEVRON_UP = "1,4.5 4.5,1 8,4.5";
const CHEVRON_DOWN = "1,0.5 4.5,4 8,0.5";

/**
 * Up to three chevrons beside a number, in one neutral colour: the direction of the chevrons is the direction of the change, how many is how much. The chevron at the TIP (the one furthest the way
 * the number moved) is the brightest and each one behind it, back toward the number, is fainter: an UP stack is brightest at the TOP, a DOWN stack brightest at the BOTTOM (Chris, 2026-10-19: "If the
 * arrows are pointing up, the brightest arrow should be at the top, the reverse is the bottom arrow is the brightest").
 */
export const TrendArrows: React.FC<{ metric: MetricKey }> = ({ metric }) => {
  const [, tick] = useState(0);
  useEffect(() => subscribe(() => tick((n) => n + 1)), []);
  const trend = state.trends?.[metric];
  if (!trend || trend.direction === "unknown") return null; // nothing to say: it could not be compared, which is not the same as "the same"
  const label = state.previous ? trendSentence(trend, valueText(metric, state.previous.metrics), previousLabel(state.previous.span, state.previous.ongoing)) : "";
  // compared and about the same (or no clear change): nothing is drawn. (A dash was tried and removed, Chris, 2026-10-19: "It looks like a literal minus sign.") The hover card still says how it compares.
  if (trend.direction === "flat" || trend.arrows === 0) return null;
  const up = trend.direction === "up";
  // arrowOpacities runs from the tip backwards: an UP stack is drawn top to bottom in that order (the tip is the top), a DOWN stack in the reverse (the tip is the bottom)
  const opacities = arrowOpacities(trend.arrows);
  const ordered = up ? opacities : opacities.slice().reverse();

  return (
    <span className={`footer-trend ${up ? "up" : "down"} arrows-${trend.arrows}`} role="img" aria-label={label || (up ? "up" : "down")} data-trend={trend.direction} data-arrows={trend.arrows}>
      {ordered.map((opacity, i) => (
        <svg key={i} width="9" height="5" viewBox="0 0 9 5" aria-hidden="true" style={{ opacity }}>
          <polyline points={up ? CHEVRON_UP : CHEVRON_DOWN} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ))}
    </span>
  );
};
